import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook to detect when tenant has been switched in another browser tab/window
 * Uses localStorage 'storage' event which fires when localStorage changes in other tabs
 * 
 * @returns {Object} { tenantChanged: boolean, changedTenantCode: string | null, originalTenantCode: string | null }
 */
export const useTenantChangeDetection = () => {
  const { getCurrentTenant } = useAuth();
  const [tenantChanged, setTenantChanged] = useState(false);
  const [changedTenantCode, setChangedTenantCode] = useState(null);
  const [originalTenantCode, setOriginalTenantCode] = useState(null);
  
  useEffect(() => {
    // Store the initial tenant code when component mounts
    const currentTenant = getCurrentTenant();
    const initialTenantCode = currentTenant?.code || currentTenant?.tenant_code || localStorage.getItem('selectedTenantCode');
    setOriginalTenantCode(initialTenantCode);
    
    console.log('🔍 TENANT_DETECTION: Initialized with tenant:', initialTenantCode);
    
    // Listen for storage changes from other tabs
    const handleStorageChange = (event) => {
      // Only care about selectedTenantCode changes
      if (event.key === 'selectedTenantCode') {
        const newTenantCode = event.newValue;
        const oldTenantCode = event.oldValue;
        
        console.log('🔍 TENANT_DETECTION: Storage change detected', {
          oldValue: oldTenantCode,
          newValue: newTenantCode,
          url: event.url,
          storageArea: event.storageArea === localStorage ? 'localStorage' : 'sessionStorage'
        });
        
        // If tenant changed in another tab and it's different from our current tenant
        if (newTenantCode && newTenantCode !== initialTenantCode) {
          console.warn('⚠️ TENANT_DETECTION: Tenant changed in another tab!', {
            originalTenant: initialTenantCode,
            newTenant: newTenantCode,
            changedFrom: oldTenantCode
          });
          
          setTenantChanged(true);
          setChangedTenantCode(newTenantCode);
          
          // Also dispatch a custom event for other components to listen to
          window.dispatchEvent(new CustomEvent('tenant:changedInAnotherTab', {
            detail: {
              oldTenantCode: oldTenantCode || initialTenantCode,
              newTenantCode: newTenantCode,
              detectedAt: new Date().toISOString()
            }
          }));
        }
      }
      
      // Also listen for token changes (more reliable indicator)
      if (event.key === 'token') {
        const newToken = event.newValue;
        const oldToken = event.oldValue;
        
        // If token changed, extract tenant code from it
        if (newToken && newToken !== oldToken) {
          try {
            const payload = JSON.parse(atob(newToken.split('.')[1]));
            const newTenantFromToken = payload.tenantCode;
            
            console.log('🔍 TENANT_DETECTION: Token change detected, tenant from token:', newTenantFromToken);
            
            if (newTenantFromToken && newTenantFromToken !== initialTenantCode) {
              console.warn('⚠️ TENANT_DETECTION: Tenant changed via token in another tab!', {
                originalTenant: initialTenantCode,
                newTenant: newTenantFromToken
              });
              
              setTenantChanged(true);
              setChangedTenantCode(newTenantFromToken);
              
              window.dispatchEvent(new CustomEvent('tenant:changedInAnotherTab', {
                detail: {
                  oldTenantCode: initialTenantCode,
                  newTenantCode: newTenantFromToken,
                  detectedAt: new Date().toISOString(),
                  via: 'token'
                }
              }));
            }
          } catch (e) {
            console.error('🔍 TENANT_DETECTION: Failed to decode token:', e);
          }
        }
      }
    };
    
    // Add event listener for storage changes
    window.addEventListener('storage', handleStorageChange);
    
    // Cleanup
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [getCurrentTenant]);
  
  const dismissWarning = () => {
    setTenantChanged(false);
  };
  
  const reloadPage = () => {
    window.location.reload();
  };
  
  return {
    tenantChanged,
    changedTenantCode,
    originalTenantCode,
    dismissWarning,
    reloadPage
  };
};

export default useTenantChangeDetection;

